// The typed HTTP client of the Rust backend's `app`, `visitor` and `admin` surfaces — the one place
// that knows how a request is authenticated, which tenant it acts in, and what a failure looks like
// (~/projects/base/frontend/architecture.md § "The API client").
//
// Domain modules beside this one are thin: one function per operation, typed from the
// generated `schema.d.ts`, no error handling of their own.
//
// These surfaces authenticate with the HttpOnly `videas_session` cookie, which no script on the
// page can read.

import type { components } from './schema'

export type ErrorBody = components['schemas']['ErrorBody']
export type ErrorCode = components['schemas']['ErrorCode']

/**
 * The surface prefix. Same origin as the page: Vite proxies it in development and the edge
 * routes it in production, so the session cookie rides every call without CORS.
 */
export const BASE = '/api/app'

/**
 * The `visitor` surface's prefix: what a caller with no account calls — an embed page, a share
 * page, and any player once it holds a `playback` token. Same origin as every page that calls it.
 */
export const VISITOR_BASE = '/api/visitor'

/**
 * The `admin` surface's prefix: what the staff console calls, on the same session cookie as `app`
 * and held to a seat on the platform rather than to a membership of an organization.
 */
export const ADMIN_BASE = '/api/admin'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The body's machine-readable failure kind. Branch on this, never on `message`. */
    readonly code?: ErrorCode,
    /** The id to quote when asking about this failure. */
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * A call that got no answer at all: `fetch` rejected — the network, a proxy that dropped the
 * connection, a server that was not there. A `TypeError` still, as `fetch` throws it, so code that
 * tested for one keeps working; but its own class, because every other bug of a script is a
 * `TypeError` too and must never be told to a person as « check your connection ».
 */
export class NetworkError extends TypeError {
  /** What `fetch` rejected with, for a developer reading the console. */
  readonly cause?: unknown

  constructor(call: string, cause?: unknown) {
    super(`${call} could not reach the server`)
    this.name = 'NetworkError'
    this.cause = cause
  }
}

/** The one code that is a state rather than a failure: a valid session with no tenant yet. */
export const NO_ORGANIZATION: ErrorCode = 'no_organization'

/** The refusal a person can act on with money: the prepaid balance does not cover the act (402). */
export const CREDITS_EXHAUSTED: ErrorCode = 'credits_exhausted'

/**
 * A good session short of the second factor a surface holds it to (403) — the `admin` surface,
 * which admits a session that proved one in the last twelve hours. Neither a sign-in nor "nothing to
 * be done": the remedy is a code, presented on the same session.
 */
export const SECOND_FACTOR_REQUIRED: ErrorCode = 'second_factor_required'

/**
 * Whether a failed read is worth asking again: a 5xx, or a request that never got an answer. A
 * 4xx is an answer, and retrying it is at best noise (~/projects/base/frontend/architecture.md
 * § "Server state vs client state").
 */
export function isRetryable(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500
}

/** How many times a failed read is asked again before its error is shown. */
export const READ_RETRIES = 3

/**
 * A query's `retry`: {@link isRetryable}'s rule, **and a bound**. A `retry` function replaces
 * TanStack's own count, so `isRetryable` alone asks a read that keeps answering 5xx for ever — and
 * everything awaiting that read (an invalidation after a write, a pending screen) waits with it.
 */
export function retryTransient(failureCount: number, error: unknown): boolean {
  return failureCount < READ_RETRIES && isRetryable(error)
}

// Session-expiry seam: a 401 means the cookie died (expired, revoked, signed out elsewhere).
// Registered from the composition root — importing the router or a store here would be a
// dependency cycle.
let onUnauthorized: (() => void) | null = null

export function setOnUnauthorized(handler: (() => void) | null): void {
  onUnauthorized = handler
}

// Tenantless seam: `403 no_organization` is NOT a session expiry. Sending that person to the
// sign-in screen would loop them through a login that changes nothing.
let onNoOrganization: (() => void) | null = null

export function setOnNoOrganization(handler: (() => void) | null): void {
  onNoOrganization = handler
}

// Prepaid-balance seam: `402 credits_exhausted` has one remedy — top up and try again — and it is
// offered in one place (`apps/billing/stores/creditsExhausted.ts`) rather than as a toast per call
// site. The error is still thrown: the caller decides whether the act may be retried, and says so
// to the same place.
let onCreditsExhausted: ((error: ApiError) => void) | null = null

export function setOnCreditsExhausted(handler: ((error: ApiError) => void) | null): void {
  onCreditsExhausted = handler
}

// Second-factor seam: `403 second_factor_required` is neither a session expiry nor a refusal. The
// staff console answers it with one screen asking for a code, whichever read met it — the bootstrap
// or a page twelve hours into a session. The error is still thrown, so the read that met it fails
// as it would and is asked again once the code convinced.
let onSecondFactorRequired: (() => void) | null = null

export function setOnSecondFactorRequired(handler: (() => void) | null): void {
  onSecondFactorRequired = handler
}

/**
 * The organization every call acts in (`X-Org-Id`). The cookie says WHO is calling; this says
 * WHERE. Module-level rather than per call, so no call site can forget it: the router sets it
 * from the `/org/:organizationId` segment before any view of that organization mounts.
 */
let activeOrgId: string | null = null

export function setActiveOrgId(orgId: string | null): void {
  activeOrgId = orgId
}

export function getActiveOrgId(): string | null {
  return activeOrgId
}

type Headers = Record<string, string>

/**
 * What one surface's calls carry. `app` authenticates a session and acts in the tenant `X-Org-Id`
 * names; `visitor` has neither, so a 401 there is a wrong password or a stale playback token rather
 * than a session that died, and it sends nobody to the sign-in screen. `admin` authenticates the
 * same session and acts in no organization: its scope is the platform the account belongs to, so
 * it never sends `X-Org-Id`, whatever the page has set — and the tenant's two states, a missing
 * organization and an empty balance, are not answers it gives.
 */
interface Surface {
  base: string
  /** A 401 is the session dying, and goes to the sign-in hook. */
  signedIn: boolean
  /** The call acts in the active organization: it carries `X-Org-Id`, and the tenant's hooks fire. */
  tenant: boolean
}

const APP: Surface = { base: BASE, signedIn: true, tenant: true }
const VISITOR: Surface = { base: VISITOR_BASE, signedIn: false, tenant: false }
const ADMIN: Surface = { base: ADMIN_BASE, signedIn: true, tenant: false }

// The browser keeps at most 64 KB of kept-alive bodies in flight, and a character of a JSON body
// is at most three bytes of it: a longer body goes as any other.
const KEEPALIVE_MAX_CHARS = 16 * 1024

/**
 * Whether a write is kept alive: one started while the page is hidden — a tab put away, or closing,
 * which sends the gestures still in their undo window (`useUndoableQueue`) — would otherwise be
 * cancelled with the page. A tab is hidden before `pagehide`, so a closing one counts.
 */
function keptAlive(init: RequestInit): boolean {
  if (typeof document === 'undefined' || document.visibilityState !== 'hidden') return false
  if (!init.method || init.method === 'GET') return false
  const body = init.body
  return body == null || (typeof body === 'string' && body.length <= KEEPALIVE_MAX_CHARS)
}

/** The shared prologue: the cookie, the tenant header, and the error mapping. */
async function send(surface: Surface, path: string, init: RequestInit = {}): Promise<Response> {
  // A FormData body must NOT get a hand-set Content-Type: the browser writes the multipart
  // boundary into it. No body at all gets none either.
  const contentType: Headers =
    init.body === undefined || init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }
  const orgHeader: Headers = surface.tenant && activeOrgId !== null ? { 'X-Org-Id': activeOrgId } : {}
  let response: Response
  try {
    response = await fetch(`${surface.base}${path}`, {
      credentials: 'same-origin',
      ...(keptAlive(init) ? { keepalive: true } : {}),
      ...init,
      // Last, so the merged set wins over whatever `init` carried.
      headers: { ...contentType, ...orgHeader, ...(init.headers as Headers | undefined) },
    })
  } catch (error) {
    // An abort is the caller's own doing and stays the `AbortError` it is.
    if (error instanceof TypeError) throw new NetworkError(`${init.method ?? 'GET'} ${path}`, error)
    throw error
  }
  if (response.ok) return response

  let body: unknown
  try {
    body = await response.json()
  } catch {
    // Not a JSON body: a proxy's page, a dropped connection. The status line is all there is.
  }
  throw failure(surface, `${init.method ?? 'GET'} ${path}`, response.status, body)
}

/** A refused call as the `ApiError` it is, with the surface's hooks told — whatever transport carried it. */
function failure(surface: Surface, call: string, status: number, body: unknown): ApiError {
  let message = `${call} -> ${status}`
  let code: ErrorCode | undefined
  let requestId: string | undefined
  if (typeof body === 'object' && body !== null) {
    if ('message' in body && typeof body.message === 'string') message = body.message
    // The vocabulary is closed and fixed by the contract (`ErrorBody.code`), so a string
    // in that field is one of its members.
    if ('code' in body && typeof body.code === 'string') code = body.code as ErrorCode
    if ('request_id' in body && typeof body.request_id === 'string') requestId = body.request_id
  }
  if (surface.signedIn && status === 401) onUnauthorized?.()
  if (surface.tenant && status === 403 && code === NO_ORGANIZATION) onNoOrganization?.()
  if (status === 403 && code === SECOND_FACTOR_REQUIRED) onSecondFactorRequired?.()
  const error = new ApiError(status, message, code, requestId)
  if (surface.tenant && status === 402 && code === CREDITS_EXHAUSTED) onCreditsExhausted?.(error)
  return error
}

/** How far a body has gone up: the bytes sent, and the bytes there are to send. */
export interface UploadProgress {
  loaded: number
  total: number
}

/**
 * A multipart `POST` whose progress is reported as it goes up — which `fetch` cannot do, so this is
 * the one call in the client carried by `XMLHttpRequest`. Same cookie, same tenant header, same
 * error mapping as every other call; an abort rejects with a `DOMException` named `AbortError`,
 * like a fetch's.
 */
function sendFormWithProgress<T>(
  surface: Surface,
  path: string,
  form: FormData,
  onProgress: (progress: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${surface.base}${path}`)
    if (surface.tenant && activeOrgId !== null) xhr.setRequestHeader('X-Org-Id', activeOrgId)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress({ loaded: event.loaded, total: event.total })
    }
    const parse = (): unknown => {
      try {
        return xhr.responseText ? JSON.parse(xhr.responseText) : undefined
      } catch {
        return undefined
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(parse() as T)
      else reject(failure(surface, `POST ${path}`, xhr.status, parse()))
    }
    xhr.onerror = () => reject(new NetworkError(`POST ${path}`))
    xhr.onabort = () => reject(new DOMException('The upload was cancelled', 'AbortError'))
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('The upload was cancelled', 'AbortError'))
        return
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }
    xhr.send(form)
  })
}

async function request<T>(surface: Surface, path: string, init?: RequestInit): Promise<T> {
  const response = await send(surface, path, init)
  // 202 and 204 carry no body, and decode to `undefined`.
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

function json(body: unknown): string | undefined {
  return body === undefined ? undefined : JSON.stringify(body)
}

/** An answer that is a document rather than JSON: its bytes, and the name the server gives it. */
export interface FileBody {
  blob: Blob
  /** The `filename` of the `Content-Disposition`, or `null` when the answer names none. */
  filename: string | null
}

/**
 * A rendering the server either answers at once — `200`, the document — or takes to do later —
 * `202`, the JSON of what it queued. The caller branches on `accepted`.
 */
export type FileOrAccepted<T> = { accepted: false; file: FileBody } | { accepted: true; body: T }

async function fileOf(response: Response): Promise<FileBody> {
  const disposition = response.headers.get('content-disposition')
  const named = disposition === null ? null : /filename="([^"]*)"/i.exec(disposition)
  return { blob: await response.blob(), filename: named?.[1] ?? null }
}

async function file(surface: Surface, path: string, init?: RequestInit): Promise<FileBody> {
  return fileOf(await send(surface, path, init))
}

async function fileOrAccepted<T>(path: string, init?: RequestInit): Promise<FileOrAccepted<T>> {
  const response = await send(APP, path, init)
  if (response.status === 202) return { accepted: true, body: (await response.json()) as T }
  return { accepted: false, file: await fileOf(response) }
}

/** A path and its query string. `undefined` and `null` are left out rather than sent empty. */
export function withQuery(path: string, params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) search.set(key, String(value))
  }
  const query = search.toString()
  return query ? `${path}?${query}` : path
}

export const api = {
  get: <T>(path: string, headers?: Headers, signal?: AbortSignal) => request<T>(APP, path, { headers, signal }),
  /** A `signal` for the reads that are a `POST` because their criteria do not fit a URL. */
  post: <T>(path: string, body?: unknown, headers?: Headers, signal?: AbortSignal) =>
    request<T>(APP, path, { method: 'POST', body: json(body), headers, signal }),
  put: <T>(path: string, body?: unknown, headers?: Headers) =>
    request<T>(APP, path, { method: 'PUT', body: json(body), headers }),
  patch: <T>(path: string, body: unknown, headers?: Headers) =>
    request<T>(APP, path, { method: 'PATCH', body: json(body), headers }),
  /** A multipart body, sent as the browser encodes it — `send` sets no `Content-Type` for one. */
  postForm: <T>(path: string, form: FormData, signal?: AbortSignal) =>
    request<T>(APP, path, { method: 'POST', body: form, signal }),
  /** The same, reporting how far the body has gone up — for a file a person watches upload. */
  postFormWithProgress: <T>(
    path: string,
    form: FormData,
    onProgress: (progress: UploadProgress) => void,
    signal?: AbortSignal,
  ) => sendFormWithProgress<T>(APP, path, form, onProgress, signal),
  /** DELETE, with a body only for the two re-authenticating deletions (account, organization). */
  del: <T>(path: string, body?: unknown, headers?: Headers) =>
    request<T>(APP, path, { method: 'DELETE', body: json(body), headers }),
  /**
   * A document this surface renders itself, read as bytes: it needs `X-Org-Id` like any other
   * call, which a plain link cannot carry. A file behind a signed URL is a link, not this.
   */
  getFile: (path: string, signal?: AbortSignal) => file(APP, path, { signal }),
  /** The same, for a rendering whose selection is a body. */
  postFile: (path: string, body: unknown) => file(APP, path, { method: 'POST', body: json(body) }),
  /** The same, for a rendering the server may queue instead: a `202` answers what it queued. */
  postFileOrAccepted: <T>(path: string, body: unknown) => fileOrAccepted<T>(path, { method: 'POST', body: json(body) }),
}

/**
 * The `visitor` surface. Its credentials are arguments — a share's password in a header, a
 * `playback_token` in a body — never the session cookie's identity or a tenant.
 */
export const visitor = {
  get: <T>(path: string, headers?: Headers, signal?: AbortSignal) => request<T>(VISITOR, path, { headers, signal }),
  post: <T>(path: string, body?: unknown, headers?: Headers) =>
    request<T>(VISITOR, path, { method: 'POST', body: json(body), headers }),
  /** Withdrawing something the caller's own credential put there — a review proposal. */
  del: <T>(path: string, headers?: Headers) => request<T>(VISITOR, path, { method: 'DELETE', headers }),
}

/**
 * The `admin` surface: the staff console's calls, on the session cookie and the account's seat on
 * its platform. Reads — as JSON, or as a document's bytes —, the code that proves the session's
 * second factor, and the staff's own writes: its notes and its tags on the platform's records, and
 * the mail it dresses a channel in.
 */
export const admin = {
  get: <T>(path: string, headers?: Headers, signal?: AbortSignal) => request<T>(ADMIN, path, { headers, signal }),
  /** A `signal` for the read that is a `POST` because its term does not belong in a URL: the search. */
  post: <T>(path: string, body: unknown, signal?: AbortSignal) =>
    request<T>(ADMIN, path, { method: 'POST', body: JSON.stringify(body), signal }),
  /**
   * A `PUT`: with no body, the address is what it writes — a tag on a record —; with one, what the
   * address holds is replaced by it — a verdict on an email domain, a version of a channel's mail
   * template.
   */
  put: <T>(path: string, body?: unknown) => request<T>(ADMIN, path, { method: 'PUT', body: json(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(ADMIN, path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(ADMIN, path, { method: 'DELETE' }),
  /**
   * A document the surface serves, read as bytes rather than linked: a refusal — a PDF not rendered
   * yet — is then an answer the page can word, not an error body opened in place of the console.
   */
  getFile: (path: string, signal?: AbortSignal) => file(ADMIN, path, { signal }),
}
